/**
 * InsightsChart.tsx
 * Feature: 155 - Telemetry Pipeline Provisioning & Management
 * Author: Peter Dobler (Dobler Data Solutions)
 * Date: 2026-05-20
 * Constitution Version: v1.12.0 | Principle XXXVII (Code Header Standards)
 *
 * Line chart showing average CU% over time for one SMA.
 */

import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { chartTooltipStyle } from '@/components/ui';
import type { InsightsHistoryPoint } from '@shared/types/telemetryPipeline';
import { formatEastern } from '@/utils/dateFormatter';

interface InsightsChartProps {
  smaSlug: string;
  series: InsightsHistoryPoint[];
}

function formatBucket(iso: string): string {
  const d = new Date(iso);
  return formatEastern(d);
}

export const InsightsChart: React.FC<InsightsChartProps> = ({ series }) => {
  if (series.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-[var(--color-text-secondary)]">
        No data for selected period
      </p>
    );
  }

  const data = series.map((pt) => ({
    bucket: formatBucket(pt.timestamp),
    cu: pt.avgCuPct,
    peak: pt.peakCuPct,
  }));

  // Peaks can exceed 100% (throttling), so scale the Y-axis to the highest peak
  // (rounded up to the nearest 25) instead of clamping at 100 and clipping the peak line.
  const maxPeak = Math.max(100, ...data.map((d) => d.peak ?? 0));
  const yMax = Math.ceil(maxPeak / 25) * 25;

  return (
    <>
      <div className="mb-1 flex items-center justify-end gap-3 text-[10px] text-[var(--color-text-secondary)]">
        <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-3" style={{ background: '#3b82f6' }} />Avg CU</span>
        <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-3 border-t border-dashed" style={{ borderColor: '#ef4444' }} />Peak CU</span>
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="bucket" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 10 }} width={40} domain={[0, yMax]} unit="%" allowDecimals={false} />
          {/* throttle threshold (80%) + the 100% saturation line when peaks run over it */}
          <ReferenceLine y={80} stroke="#f59e0b" strokeDasharray="4 2" />
          {yMax > 100 && <ReferenceLine y={100} stroke="#ef4444" strokeDasharray="2 2" />}
          <Tooltip
            {...chartTooltipStyle}
            formatter={(v: unknown, name: unknown) => [v == null ? '—' : `${(v as number).toFixed(1)}%`, name === 'peak' ? 'Peak CU' : 'Avg CU']}
          />
          {/* Peak drawn first (under the avg line) so the primary avg trend stays legible on top. */}
          <Line type="monotone" dataKey="peak" name="peak" stroke="#ef4444" strokeDasharray="4 2" dot={false} strokeWidth={1.5} connectNulls />
          <Line type="monotone" dataKey="cu" name="cu" stroke="#3b82f6" dot={false} strokeWidth={2} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};

export default InsightsChart;
